









import Vue from 'vue'
import { debounce } from '~/utils/function'
import { v4 as uuidv4 } from 'uuid'
import { addButton } from './leaflet'
import { Geolocation } from '~/models/common/types'

export default Vue.extend({
  props: {
    buttons: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    focusedListing: {
      type: Object,
      required: false,
      default() {
        return null
      }
    },
    focusedGeolocation: {
      type: Object,
      required: false,
      default() {
        return null
      }
    },
    markers: {
      type: Array,
      required: true
    },
    markerClickContentProvider: {
      type: Function,
      required: false,
      default() {
        return null
      }
    },
    markerClickPendingContentProvider: {
      type: Function,
      required: false,
      default() {
        return null
      }
    },
    hideOnInitZoom: {
      type: Boolean,
      required: false,
      default: true
    },
    maxBounds: {
      type: Object,
      required: false,
      default: null
    }
  },
  data: () => ({
    id: 'carzillaClusterMap',
    allMarkers: [],
    initBoundariesChanged: true,
    facets: {
      bounds: {
        topRight: { lat: null, lon: null },
        bottomLeft: { lat: null, lon: null }
      }
    },
    loading: false,
    zoom: null,
    initialLoad: true,
    retryCount: 0,
    map: null,
    L: null
  }),
  computed: {
    mapZoom() {
      return this.map && this.map.getZoom()
    },
    maxBoundsLatLng() {
      return (
        this.L &&
        this.L.latLngBounds(
          this.L.latLng(this.maxBounds.lat.max, this.maxBounds.lon.max),
          this.L.latLng(this.maxBounds.lat.min, this.maxBounds.lon.min)
        )
      )
    }
  },
  watch: {
    markers(newMarkers, oldMarkers) {
      const markersToInsert = newMarkers.filter(
        marker => !oldMarkers.some(oldMarker => oldMarker.v === marker.v)
      )
      const markersToRemove = oldMarkers.filter(
        marker => !newMarkers.some(newMarker => newMarker.v === marker.v)
      )
      this.removeMarkers(markersToRemove)
      this.insertMarkers(markersToInsert)
    },
    focusedGeolocation(focusedGeolocation: Geolocation) {
      this.flyTo(focusedGeolocation.lat, focusedGeolocation.lon, 11)
    },
    focusedListing(focusedListing) {
      if (focusedListing && focusedListing.geolocation && !this.focusMarker) {
        const markerSize = this.getSizeByListingName(focusedListing.displayName)
        const icon = new this.L.DivIcon({
          iconSize: new this.L.Point(markerSize, 23),
          iconAnchor: [markerSize / 2, 12],
          className: 'marker-div marker-div--active',
          html: focusedListing.displayName
        })

        this.focusMarker = this.L.marker(
          [focusedListing.geolocation.lat, focusedListing.geolocation.lon],
          { icon }
        )
        this.focusMarker.setZIndexOffset(99999).addTo(this.map)
      } else if (this.focusMarker) {
        this.map.removeLayer(this.focusMarker)
        this.focusMarker = null
      }
    }
  },
  async mounted() {
    await import('leaflet')
    await import('leaflet.markercluster')
    await this.$nextTick()
    this.L = window.L
    this.generateRandomId()
    this.allMarkers = this.allMarkers.concat(this.markers)
    this.initializeMap()
    this.initializeClusters()
    this.placeButtons()
    this.setupEventListeners()
    this.handleUrlGeolocation()
    this.insertMarkers(this.markers)
    this.loading = false
    await this.$nextTick()
    this.map.invalidateSize()
  },
  methods: {
    visibilityChanged(visibility) {
      if (visibility && this.map) {
        this.invalidateSize()
      }
    },
    invalidateSize() {
      this.map.invalidateSize(false)
    },
    generateRandomId() {
      this.id = uuidv4()
    },
    initializeClusters() {
      this.activeMarkersGroup = this.L.markerClusterGroup({
        spiderfyOnMaxZoom: false,
        disableClusteringAtZoom: 18,
        chunkedLoading: true,
        maxClusterRadius: 50,
        removeOutsideVisibleBounds: true,
        iconCreateFunction(cluster) {
          let childCount = cluster.getChildCount()

          childCount = childCount > 50 ? childCount + '+' : childCount
          let c = ' marker-cluster-'
          if (childCount < 10) {
            c += 'small'
          } else if (childCount < 100) {
            c += 'medium'
          } else {
            c += 'large'
          }

          return new window.L.DivIcon({
            html: '<div><span>' + childCount + '</span></div>',
            className: 'marker-cluster' + c,
            iconSize: new window.L.Point(40, 40)
          })
        }
      })
    },
    flyTo(lat, lng, zoom = 12) {
      this.map.flyTo([lat, lng], zoom)
    },
    getSizeByListingName(listingName) {
      let size = 7 * Math.max(('' + listingName.substring(0, 19)).length, 15)
      if (size > 160) size = 160
      if (size < 15) size = 60
      return size
    },
    clearMarkers() {
      this.activeMarkersGroup.clearLayers()
    },
    removeMarkers(markersToRemove) {
      this.activeMarkersGroup.eachLayer(marker => {
        if (
          markersToRemove.some(
            markerToRemove => markerToRemove.v === marker.options.id
          )
        ) {
          this.activeMarkersGroup.removeLayer(marker)
        }
      })
    },
    handleUrlGeolocation() {
      const url = new URL(window.location.href)
      const geolocation = url.searchParams.getAll('geolocation')
      let points = null

      if (geolocation && geolocation.length > 0) {
        for (const i in geolocation) {
          try {
            points = geolocation[i].split(',')
          } catch (err) {
            return
          }
        }
      }

      if (points && points.length === 2) {
        const bound1 = points[0].split('x')
        const topRight = { lat: bound1[0], lon: bound1[1] }

        const bound2 = points[1].split('x')
        const bottomLeft = { lat: bound2[0], lon: bound2[1] }

        const bounds = this.L.latLngBounds([
          [topRight.lat, topRight.lon],
          [bottomLeft.lat, bottomLeft.lon]
        ])

        this.map.fitBounds(bounds)
      } else {
        this.map.setView([38.25, 23.75], this.zoom)
      }
    },
    initializeMap() {
      this.L.Control.include({
        _refocusOnMap: this.L.Util.falseFn // Do nothing. (fix for zoom at android chrome)
      })

      this.zoom = window.innerWidth > 991 ? 7 : 6
      const tiles = this.L.tileLayer(
        'https://map.pkw.gr/osm_tiles/{z}/{x}/{y}.png',
        {
          attribution:
            '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
          minZoom: this.zoom
        }
      )
      this.map = this.L.map('mapContainer', {
        preferCanvas: true,
        attributionControl: false,
        layers: [tiles]
      })
      if (this.maxBounds) {
        this.map.setMaxBounds(this.maxBoundsLatLng)
      }
    },
    placeButtons() {
      this.buttons.forEach(button => {
        addButton({ L: this.L, map: this.map }, button)
      })
    },
    setupEventListeners() {
      this.map.on('drag', _ => {
        if (this.maxBoundsLatLng) {
          this.map.panInsideBounds(this.maxBoundsLatLng)
        }
      })
      this.map.on('zoomend', () => {
        this.zoom = this.map.getZoom()
        if (this.focusMarker) {
          this.map.removeLayer(this.focusMarker)
          this.focusMarker = null
        }
      })
      this.map.on(
        'moveend',
        debounce(_ => {
          if (this.initBoundariesChanged) {
            this.initBoundariesChanged = false
          } else {
            const southwest = this.map.getBounds()._southWest
            const northeast = this.map.getBounds()._northEast

            this.mapBoundsChanged({
              topRight: {
                lat: northeast.lat.toFixed(8),
                lon: northeast.lng.toFixed(8)
              },
              bottomLeft: {
                lat: southwest.lat.toFixed(8),
                lon: southwest.lng.toFixed(8)
              }
            })

            this.zoom = this.map.getZoom()
          }
          this.$emit('move', 'end')
        }, 400)
      )

      this.map.on('movestart', _ => {
        this.$emit('move', 'start')
      })
    },
    fitBounds(bounds) {
      this.map.fitBounds(bounds)
    },
    mapBoundsChanged(bounds) {
      this.$emit('update:bounds', bounds)
    },
    insertMarkers(markers) {
      if (markers.length > 0) {
        markers.forEach(m => {
          if (m.g == null) return
          const size = this.getSizeByListingName(m.n)
          const icon = new this.L.DivIcon({
            iconSize: new this.L.Point(size, 23),
            iconAnchor: [size / 2, 12],
            className: 'marker-div marker-div--normal',
            html: m.n,
            id: m.v
          })
          const marker = this.L.marker(new this.L.LatLng(m.g[0], m.g[1]), {
            icon,
            id: m.v
          })
            .bindPopup('', {
              autoPan: true
            })
            .on('click', _ => {
              const listingId = m.v
              this.createMarkerPopup(marker, listingId)
            })
          this.activeMarkersGroup.addLayer(marker)
        })
        this.map.addLayer(this.activeMarkersGroup)
        this.$emit('update:markers')
      }
    },
    createMarkerPopup(marker, listingId) {
      const popup = marker.getPopup()
      if (this.markerClickContentProvider) {
        if (this.markerClickPendingContentProvider) {
          popup.setContent(this.markerClickPendingContentProvider())
        }
        this.markerClickContentProvider(listingId).then(content => {
          popup.setContent(content)
          popup.update()
          popup.openPopup()
        })
      }
    }
  }
})
